import React, { useState, useEffect, useRef, useMemo } from "react";
import { CircularProgress, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import TrafficList from "./TrafficList";
import useNetworkData from "../data/useNetworkData";
import useEventsMonioringData from "../data/useEventsMonioringData";
import TrafficMonitoringMap from "./trafficMonitoringMap/TrafficMonitoringMap";
import useTrafficMonitoringGeom from "../data/useTrafficMonitoringGeom";

// Memoized components to prevent unnecessary remounts
const MemoTrafficList = React.memo(TrafficList);
const MemoTrafficMonitoringMap = React.memo(TrafficMonitoringMap);

function NetworkView() {
  // Hooks: state, refs, memos, effects
  const { data, loading } = useNetworkData();
  const { events, loading: eventsLoading, error } = useEventsMonioringData();
  const {
    data: networkData,
    loading: networkLoading,
  } = useTrafficMonitoringGeom();

  const hasLoadedOnce = useRef(false);

  const [onlyActive, setOnlyActive] = useState([]);
  const [onlyJam, setOnlyJam] = useState([]);
  const [networkSource, setNetworkSource] = useState([]);

  // Derive active & jam events
  useEffect(() => {
    if (Array.isArray(events) && events.length > 0) {
      setOnlyActive(events);
      setOnlyJam(events.filter((e) => e.category?.id === 10));
    }
  }, [events]);

  // Update GeoJSON source for the map
  useEffect(() => {
    if (networkData) {
      setNetworkSource(networkData);
    }
  }, [networkData]);

  // Memoize statistics arrays to keep prop identity stable
  const dataOfEvents = useMemo(
    () => [
      {
        name: "Number of Events",
        values: [
          { label: "Total", value: onlyActive.length, status: "neutral" },
        ],
        description: `Total <strong>number of active events</strong> across the monitored network, including accidents, roadworks, weather impacts, or other traffic-affecting incidents.`,
      },
    ],
    [onlyActive.length]
  );

  const dataOfJams = useMemo(
    () => [
      {
        name: "Number of Traffic Jams",
        values: [{ label: "Total", value: onlyJam.length, status: "neutral" }],
        description: `Indicates how many distinct <strong>traffic jams</strong> are currently identified in the network.`,
      },
    ],
    [onlyJam.length]
  );

   // **Combine all three into one array**
  const allMetrics = useMemo(
    () => [...(data || []), ...dataOfEvents, ...dataOfJams],
    [data, dataOfEvents, dataOfJams]
  );



  // Loading guard: only on first load
  const anyLoading = loading || eventsLoading || networkLoading;
  if (anyLoading && !hasLoadedOnce.current) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100%" }}
      >
        <CircularProgress />
      </Grid>
    );
  }
  hasLoadedOnce.current = true;

  // Render
return (
  <Grid container spacing={2}>
    {/* Statistic List */}
    <Grid
      item
      xs={12}
      md={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",       // match map height
        maxHeight: "90vh",    // optional cap
        py: 1,               // a little vertical padding
      }}
    >
      <MemoTrafficList data={allMetrics} />
    </Grid>

    {/* Map Display */}
    <Grid item xs={12} md={9}>
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",      // same viewport height
          maxHeight: "90vh",
        }}
      >
        <MDBox
          sx={{
            flexGrow: 1,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <MemoTrafficMonitoringMap
            onlyActive={onlyActive}
            networkSource={networkSource}
            areaRectSource={[]}
          />
        </MDBox>
      </MDBox>
    </Grid>
  </Grid>
);

}

export default NetworkView;
